import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { KpiTile } from "@/components/KpiTile";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps } from "@/lib/chart";
import { axisINR, formatPercent, formatCount, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  ScatterChart, Scatter, ZAxis,
} from "recharts";
import {
  Store, Wallet, CheckCircle2, Gauge, Search, Download,
  TrendingUp, ShieldCheck,
} from "lucide-react";

const PERIODS = ["FY", "Q1", "Q2", "Q3", "Q4"];

export const GRADE_META = {
  A: { color: "hsl(var(--success))", chip: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.32)]", label: "Efficient" },
  B: { color: "hsl(var(--info))", chip: "bg-[hsl(var(--info)/0.14)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.32)]", label: "Moderate" },
  C: { color: "hsl(var(--warning))", chip: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.34)]", label: "Watch" },
};

function GradeBadge({ grade }) {
  const m = GRADE_META[grade] || GRADE_META.C;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold", m.chip)}
      data-testid={`vendor-grade-${grade}`}>
      <ShieldCheck className="h-3 w-3" /> {grade}
    </span>
  );
}

const SORTS = [
  { value: "spend", label: "Total Spend" },
  { value: "outstanding", label: "Outstanding" },
  { value: "efficiency", label: "Efficiency" },
  { value: "paid_ratio", label: "Paid %" },
  { value: "bills", label: "Bill Count" },
];

export default function VendorScorecards() {
  const { money } = useCurrency();
  const [period, setPeriod] = useState("FY");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("spend");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.vendorScorecards(period)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Could not load vendor scorecards"); });
  };
  useEffect(load, [period]);

  const vendors = useMemo(() => data?.vendors || [], [data]);

  const filtered = useMemo(() => {
    let r = vendors;
    if (gradeFilter !== "all") r = r.filter((v) => v.grade === gradeFilter);
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((v) => (v.vendor || "").toLowerCase().includes(q));
    return [...r].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  }, [vendors, gradeFilter, search, sortBy]);

  const topBar = useMemo(
    () => vendors.slice(0, 12).map((v) => ({ name: v.vendor, spend: v.spend, grade: v.grade })).reverse(),
    [vendors]
  );
  const scatter = useMemo(
    () => vendors.slice(0, 60).map((v) => ({ x: v.spend, y: Math.round(v.efficiency * 100), z: v.bills, name: v.vendor, grade: v.grade })),
    [vendors]
  );

  const exportCsv = () => {
    if (!vendors.length) return;
    const head = ["Rank", "Vendor", "Total Spend", "Bills", "Avg Bill", "Paid", "Outstanding", "Paid %", "Performance Share %", "Efficiency %", "Grade", "Projects", "Verticals", "Top Platform"];
    const lines = [head.join(",")];
    vendors.forEach((v) => {
      lines.push([
        v.rank, `"${(v.vendor || "").replace(/"/g, '""')}"`, v.spend, v.bills, v.avg_bill, v.paid, v.outstanding,
        (v.paid_ratio * 100).toFixed(1), (v.performance_share * 100).toFixed(1), (v.efficiency * 100).toFixed(1),
        v.grade, v.projects, v.verticals, `"${v.top_platform || ""}"`,
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vendor_scorecards_${period}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Vendor scorecards exported");
  };

  if (loading && !data) return <Loading />;
  if (!data || vendors.length === 0) return (
    <div className="space-y-5" data-testid="vendor-scorecards-page">
      <PageHeader title="Vendor Scorecards" description="Every vendor's spend, payment health and cost-efficiency on one comparison screen." />
      <EmptyState title="No vendor spend yet" message="Vendor scorecards appear once spends are recorded for this period." />
    </div>
  );

  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="vendor-scorecards-page">
      <PageHeader title="Vendor Scorecards" description="Every vendor's spend, payment health and cost-efficiency on one comparison screen.">
        <Button variant="outline" onClick={exportCsv} data-testid="vendor-export-button">
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[130px]" data-testid="vendor-period-selector"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p} data-testid={`vendor-period-${p}`}>{p === "FY" ? "Full Year" : p}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <KpiTile testId="kpi-vendor-spend" title="Total Vendor Spend" accent="hsl(var(--chart-cat-2))" icon={Store} value={<Money value={t.spend} />} sub={`${formatCount(t.vendor_count)} vendors`} />
        <KpiTile testId="kpi-vendor-paid" title="Paid" accent="hsl(var(--success))" icon={CheckCircle2} value={formatPercent(t.paid_ratio)} sub={<><Money value={t.paid} /> cleared</>} />
        <KpiTile testId="kpi-vendor-outstanding" title="Outstanding" accent="hsl(var(--warning))" icon={Wallet} value={<Money value={t.outstanding} />} sub="Yet to be paid" />
        <KpiTile testId="kpi-vendor-efficiency" title="Avg Efficiency" accent="hsl(var(--primary))" icon={Gauge} value={formatPercent(t.avg_efficiency)} sub={`A ${t.grades.A} · B ${t.grades.B} · C ${t.grades.C}`} />
        <KpiTile testId="kpi-vendor-top" title="Biggest Vendor" accent="hsl(var(--chart-cat-6))" icon={TrendingUp} value={<span className="truncate text-base" title={t.top_vendor}>{t.top_vendor}</span>} sub="By total spend" />
      </div>

      {/* charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title="Top Vendors by Spend" description="Largest payees this period, coloured by efficiency grade." testId="card-vendor-top">
          <div className="px-2 py-4" style={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topBar} layout="vertical" margin={{ top: 0, right: 20, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }}
                  tickFormatter={(v) => (v.length > 18 ? `${v.slice(0, 18)}…` : v)} />
                <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]} barSize={14} isAnimationActive={false}>
                  {topBar.map((v) => <Cell key={v.name} fill={(GRADE_META[v.grade] || GRADE_META.C).color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Spend vs Cost-Efficiency" description="Big spenders that sit low on efficiency deserve a closer look." testId="card-vendor-scatter">
          <div className="px-2 py-4" style={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, left: 8, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                <XAxis type="number" dataKey="x" name="Spend" tickFormatter={axisINR} {...axisProps} />
                <YAxis type="number" dataKey="y" name="Efficiency" unit="%" domain={[0, 100]} {...axisProps} />
                <ZAxis type="number" dataKey="z" range={[40, 260]} name="Bills" />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
                      <p className="mb-1 font-semibold">{p.name}</p>
                      <p className="text-muted-foreground">Spend: <span className="font-medium text-foreground">{money(p.x)}</span></p>
                      <p className="text-muted-foreground">Efficiency: <span className="font-medium text-foreground">{p.y}%</span></p>
                      <p className="text-muted-foreground">Bills: <span className="font-medium text-foreground">{p.z}</span></p>
                    </div>
                  );
                }} />
                <Scatter name="Vendors" data={scatter} isAnimationActive={false}>
                  {scatter.map((v, i) => <Cell key={i} fill={(GRADE_META[v.grade] || GRADE_META.C).color} fillOpacity={0.75} />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* comparison table */}
      <SectionCard
        title="Vendor Comparison"
        description={`${filtered.length} of ${vendors.length} vendors — click a row to see payment & efficiency detail.`}
        testId="card-vendor-table"
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendor…" className="h-9 w-[200px] pl-8" data-testid="vendor-search" />
            </div>
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="vendor-grade-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                <SelectItem value="A">Grade A</SelectItem>
                <SelectItem value="B">Grade B</SelectItem>
                <SelectItem value="C">Grade C</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="vendor-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>Sort: {s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        }
      >
        <div className="table-scroll" style={{ maxHeight: 560 }}>
          <table className="w-full border-collapse">
            <thead><tr>
              {["#", "Vendor", "Total Spend", "Bills", "Paid %", "Outstanding", "Perf. Share", "Efficiency", "Grade"].map((h, i) => (
                <th key={h} className={cn("th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                  i === 1 ? "th-sticky-left text-left" : i === 0 ? "text-left" : i === 8 ? "text-center" : "text-right")}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-sm text-muted-foreground">No vendors match this filter.</td></tr>
              ) : filtered.map((v, i) => (
                <tr key={v.vendor} data-testid={`vendor-row-${i}`} onClick={() => setSelected(v)}
                  className={cn("cursor-pointer border-b last:border-0 hover:bg-[hsl(var(--primary)/0.05)]", i % 2 && "row-zebra")}>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground tabular-nums">{v.rank}</td>
                  <td className="td-sticky-left px-3 py-2.5">
                    <p className="text-sm font-medium">{v.vendor}</p>
                    <p className="text-[11px] text-muted-foreground">{v.projects} project(s) · {v.top_platform || "—"}</p>
                  </td>
                  <td className="px-3 py-2.5 num text-sm font-medium"><Money value={v.spend} /></td>
                  <td className="px-3 py-2.5 num text-sm text-muted-foreground">{formatCount(v.bills)}</td>
                  <td className="px-3 py-2.5 num text-sm">
                    <span className={v.paid_ratio >= 0.5 ? "text-[hsl(var(--success))]" : v.paid_ratio > 0 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--danger))]"}>{formatPercent(v.paid_ratio, 0)}</span>
                  </td>
                  <td className="px-3 py-2.5 num text-sm"><Money value={v.outstanding} /></td>
                  <td className="px-3 py-2.5 num text-sm text-muted-foreground">{formatPercent(v.performance_share, 0)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, v.efficiency * 100)}%`, background: (GRADE_META[v.grade] || GRADE_META.C).color }} />
                      </div>
                      <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">{formatPercent(v.efficiency, 0)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center"><GradeBadge grade={v.grade} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <VendorDetailSheet vendor={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function VendorDetailSheet({ vendor, onClose }) {
  if (!vendor) return null;
  const m = GRADE_META[vendor.grade] || GRADE_META.C;
  return (
    <Sheet open={!!vendor} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="vendor-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 pr-6">{vendor.vendor} <GradeBadge grade={vendor.grade} /></SheetTitle>
          <SheetDescription>
            Rank #{vendor.rank} · {vendor.projects} project(s) · {vendor.verticals} vertical(s) · last activity {formatDate(vendor.last_date)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {[
            { label: "Total Spend", node: <Money value={vendor.spend} /> },
            { label: "Bills", node: formatCount(vendor.bills) },
            { label: "Paid", node: <Money value={vendor.paid} /> },
            { label: "Outstanding", node: <Money value={vendor.outstanding} /> },
            { label: "Avg Bill", node: <Money value={vendor.avg_bill} /> },
            { label: "Top Platform", node: vendor.top_platform || "—" },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2">
              <p className="text-[11px] text-muted-foreground">{k.label}</p>
              <p className="mt-0.5 text-sm font-semibold tabular-nums">{k.node}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg border p-3">
          <p className="text-xs font-semibold text-muted-foreground">Cost-Efficiency ({m.label})</p>
          <div className="mt-2 space-y-2 text-xs">
            <EffBar label="Performance share" pct={vendor.performance_share} color="hsl(var(--primary))" />
            <EffBar label="Payment discipline" pct={vendor.paid_ratio} color="hsl(var(--success))" />
            <EffBar label="Overall efficiency" pct={vendor.efficiency} color={m.color} />
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment status breakdown</p>
          <div className="rounded-lg border">
            <table className="w-full border-collapse">
              <tbody>
                {vendor.status_breakdown.map((s) => (
                  <tr key={s.status} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs">{s.status}</td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">{s.count}×</td>
                    <td className="px-3 py-2 text-right text-xs font-medium tabular-nums"><Money value={s.amount} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EffBar({ label, pct, color }) {
  const w = Math.min(100, Math.max(0, (pct || 0) * 100));
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{formatPercent(pct, 0)}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full" style={{ width: `${w}%`, background: color }} />
      </div>
    </div>
  );
}
